/** Strategy PermissionLevel enum (API numeric values). */
export const PERMISSION_LEVEL = {
  None: 0,
  List: 1,
  ReadNarrow: 2,
  Read: 3,
  Write: 4,
};

export const PERMISSION_LEVEL_OPTIONS = [
  { value: PERMISSION_LEVEL.List, label: "List" },
  { value: PERMISSION_LEVEL.ReadNarrow, label: "Read narrow" },
  { value: PERMISSION_LEVEL.Read, label: "Read" },
  { value: PERMISSION_LEVEL.Write, label: "Write" },
];

export const permissionLevelLabel = (level) => {
  const n = Number(level);
  const found = PERMISSION_LEVEL_OPTIONS.find((o) => o.value === n);
  if (found) return found.label;
  if (n === PERMISSION_LEVEL.None) return "None";
  return String(level ?? "-");
};

export const normalizePermissions = (permissions) => ({
  default: Number(permissions?.default ?? PERMISSION_LEVEL.List),
  user: { ...(permissions?.user || {}) },
  manager: { ...(permissions?.manager || {}) },
});

/** Diff baseline → draft into a patch of only changed entries (0 = remove). */
export const buildPermissionsPatch = (baseline, draft) => {
  const base = normalizePermissions(baseline);
  const next = normalizePermissions(draft);
  const patch = {};

  if (next.default !== base.default) {
    patch.default = next.default;
  }

  const userPatch = {};
  const userKeys = new Set([
    ...Object.keys(base.user),
    ...Object.keys(next.user),
  ]);
  userKeys.forEach((key) => {
    const from = base.user[key];
    const to = next.user[key];
    if (to === undefined && from !== undefined) {
      userPatch[key] = PERMISSION_LEVEL.None;
    } else if (to !== undefined && Number(to) !== Number(from)) {
      userPatch[key] = Number(to);
    }
  });
  if (Object.keys(userPatch).length) patch.user = userPatch;

  const managerPatch = {};
  const managerKeys = new Set([
    ...Object.keys(base.manager),
    ...Object.keys(next.manager),
  ]);
  managerKeys.forEach((key) => {
    const from = base.manager[key];
    const to = next.manager[key];
    if (to === undefined && from !== undefined) {
      managerPatch[key] = PERMISSION_LEVEL.None;
    } else if (to !== undefined && Number(to) !== Number(from)) {
      managerPatch[key] = Number(to);
    }
  });
  if (Object.keys(managerPatch).length) patch.manager = managerPatch;

  return Object.keys(patch).length ? patch : null;
};

/** Owner or Write (4) on the strategy can edit permissions. */
export const canEditStrategyPermissions = (strategy, user, managers) => {
  if (!strategy) return false;
  const username = user?.username;
  const owner = strategy.owner || "";
  if (username && owner === `user#${username}`) return true;
  const managerId = managers?.[0]?.managerId;
  if (managerId && owner === `manager#${managerId}`) return true;
  const level = strategy.permissions?.user?.[username];
  return Number(level) === PERMISSION_LEVEL.Write;
};
